import { useMemo, useState } from "react";
import { X, Upload } from "lucide-react";
import { api } from "@/lib/api";

/**
 * MediaPicker — modal de sélection d'un média depuis la bibliothèque admin.
 * Réutilisé par le GuideEditor et par la config Bons cadeaux.
 *
 * Props :
 *   uploaded    — liste actuelle des fichiers (api.listMedia)
 *   onPick      — callback(filename) quand on sélectionne un fichier
 *   onClose     — fermer le modal
 *   onUploaded  — appelé après un upload libre pour rafraîchir la liste
 *   site        — code du site (par défaut "refuge")
 *   accept      — attribut accept du <input type="file">
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function MediaPicker({
  uploaded, onPick, onClose, onUploaded, site = "refuge", accept = "image/*,video/*",
}) {
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const filtered = useMemo(
    () => uploaded.filter((f) => f.filename.toLowerCase().includes(q.toLowerCase())),
    [uploaded, q]
  );

  const uploadFree = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      const clean = file.name
        .toLowerCase()
        .replace(/\.[^.]+$/, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 40);
      const ext = (file.name.split(".").pop() || "bin").toLowerCase();
      const ts = new Date().toISOString().replace(/[:T-]/g, "").slice(0, 14);
      const filename = `custom-${ts}-${clean || "media"}.${ext}`;
      await api.uploadMedia(file, filename, site);
      await onUploaded();
      onPick(filename);
    } catch (er) {
      setErr(er?.response?.data?.detail || er.message || "Erreur d'upload");
    } finally {
      setUploading(false);
    }
  };

  const isVideo = (fn) => /\.(mp4|webm|mov|ogg|ogv|m4v)$/i.test(fn);

  return (
    <div
      data-testid="media-picker"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[86vh] bg-white rounded-2xl overflow-hidden flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-refuge-border">
          <p className="font-heading text-lg font-light text-refuge-text">Choisir un média</p>
          <button type="button" onClick={onClose} className="p-1.5 text-refuge-soft hover:text-refuge-ruby">
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-3 border-b border-refuge-border/60 flex gap-2">
          <input
            type="text"
            placeholder="Rechercher un fichier…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 px-3 py-2 border border-refuge-border rounded-lg text-sm font-body"
          />
          <label
            className={
              "inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-xs font-body cursor-pointer shrink-0 " +
              (uploading ? "bg-refuge-ruby/60 cursor-wait" : "bg-refuge-ruby hover:bg-refuge-ruby/90")
            }
            data-testid="picker-upload-free"
          >
            <Upload size={12} />
            {uploading ? "Upload…" : "Ajouter un fichier"}
            <input
              type="file"
              accept={accept}
              onChange={uploadFree}
              className="hidden"
              disabled={uploading}
            />
          </label>
        </div>
        {err && <div className="px-5 py-2 text-refuge-ruby text-sm">{err}</div>}
        <div className="p-4 overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <p className="text-center py-10 text-refuge-soft italic text-sm">
              Aucun média trouvé. Utilise « Ajouter un fichier » pour uploader une nouvelle image ou vidéo.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filtered.map((f) => {
                const src = `${BACKEND}/api/media/${encodeURIComponent(f.filename)}?v=${encodeURIComponent(f.uploadDate || "")}`;
                return (
                  <button
                    type="button"
                    key={f.id || f.filename}
                    onClick={() => onPick(f.filename)}
                    data-testid={`picker-item-${f.filename}`}
                    className="group rounded-lg overflow-hidden border border-refuge-border hover:border-refuge-ruby text-left transition-all"
                  >
                    <div className="aspect-square bg-refuge-bg overflow-hidden flex items-center justify-center">
                      {isVideo(f.filename) ? (
                        <video
                          src={src}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                          preload="metadata"
                        />
                      ) : (
                        <img
                          src={src}
                          alt={f.filename}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      )}
                    </div>
                    <p className="px-2 py-1.5 font-mono text-[9px] text-refuge-soft truncate" title={f.filename}>
                      {f.filename}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
